import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Popover, Button, Space, Badge, List, Avatar, Skeleton } from 'antd';
import {
    BellOutlined,
    ReloadOutlined,
    CheckOutlined,
} from '@ant-design/icons';
import { Link, useLocation } from 'react-router-dom';
import InfiniteScroll from 'react-infinite-scroll-component';
import HtmlParser from 'react-html-parser';
import moment from 'moment';
import qs from 'qs';

import logo_avatar from '@/assets/img/Logo-chingo-dental.png';
import firebaseService from '@/utils/firebaseService';
import audioFile from '@/assets/audio/message_received.mp3';

import {
    fetchNotifications,
    fetchNotificationsSummary,
    markNotificationRead,
    markAllNotificationsRead,
    addDeviceToken,
    markNotificationUnread
} from '@/redux/actions/notificationsActions';
import { isMobile } from 'react-device-detect';

const getNotificationPreviewMessage = (item) => {
    if (item?.short_message) {
        return item.short_message;
    }

    const message = item?.message || '';

    if (item?.module_key !== 'commission') {
        return message;
    }

    const match = String(message).match(/<div[^>]*>[\s\S]*?<\/div>/i);
    return match ? match[0] : message;
};

const NotificationPopover = () => {
    const dispatch = useDispatch();
    const location = useLocation();

    const notifications = useSelector(
        (state) => state.notificationsReducer.summary.data
    );
    const pagination = useSelector(
        (state) => state.notificationsReducer.summary.pagination
    );
    const unreadCount = useSelector(
        (state) => state.notificationsReducer.summary.unread_count
    );

    const [visible, setVisible] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const audioRef = useRef(new Audio(audioFile));

    /** Khởi tạo */
    useEffect(() => {

        if (!isMobile) { // Mobile không hỗ trợ Firebase Messaging
            initFirebase();
        }
        loadNotifications(qs.parse(location.search.slice(1)));

        document.addEventListener('visibilitychange', handleTabFocus);
        return () => {
            document.removeEventListener('visibilitychange', handleTabFocus);
        };
    }, []);

    /** Lấy token & lắng nghe push */
    const initFirebase = () => {
        firebaseService.fetchToken((token) => {
            if (token) dispatch(addDeviceToken({ device_token: token }));
        });

        firebaseService.onMessageListener(() => {
            loadNotifications();
            playSound();
        });
    };

    const playSound = () => {
        try {
            const audio = audioRef.current;
            audio.pause();
            audio.currentTime = 0;
            audio.play();
        } catch (err) {
            console.warn('Cannot play sound automatically:', err.message);
        }
    };

    /** Tải danh sách */
    const loadNotifications = useCallback(
        (query = qs.parse(window.location.search.slice(1)), isLoadMore = false) => {
            console.log('query: ', query);
            const nextPage = isLoadMore
                ? (pagination.currentPage || 1) + 1
                : 1;

            setIsLoading(true);
            Promise.all([
                dispatch(fetchNotificationsSummary({ page: nextPage })),
                dispatch(fetchNotifications(query)),
            ]).finally(() => setIsLoading(false));
        },
        [dispatch, pagination]
    );

    const handleTabFocus = useCallback(() => {
        if (!document.hidden) {
            const query = qs.parse(window.location.search.slice(1));
            loadNotifications(query);
        }
    }, [loadNotifications]);

    /** Sự kiện người dùng */
    const handleMarkRead = (id) => {
        dispatch(markNotificationRead(id)).then(() => loadNotifications());
    };

    const handleMarkAll = () => {
        dispatch(markAllNotificationsRead()).then(() => loadNotifications());
    };

    const handleMarkUnread = (id) => {
        dispatch(markNotificationUnread(id))
            .then(() => loadNotifications());
    };

    const handleReload = () => loadNotifications();

    const handleScrollLoad = () => loadNotifications(undefined, true);

    const toggleVisible = (v) => {
        setVisible(v);
        if (v) loadNotifications();
    };

    return (
        <Popover
            className="header-actions"
            placement="bottomRight"
            trigger="click"
            open={visible}
            onOpenChange={toggleVisible}
            content={
                <div
                    style={{
                        width: isMobile ? '100%' : 400,
                        maxHeight: '88vh',
                        background: '#fff',
                        borderRadius: 6,
                        overflow: 'hidden',
                    }}
                >
                    {/* Header */}
                    <div
                        style={{
                            position: 'sticky',
                            top: 0,
                            zIndex: 10,
                            background: '#fff',
                            borderBottom: '1px solid #f0f0f0',
                            padding: '8px 10px',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
                        }}
                    >
                        <Space
                            align="center"
                            style={{ justifyContent: 'space-between', width: '100%' }}
                        >
                            <h5 style={{ margin: 0, fontSize: 15 }}>
                                <BellOutlined /> Thông báo
                            </h5>
                            <Space>
                                <Button
                                    size="small"
                                    icon={<ReloadOutlined />}
                                    onClick={handleReload}
                                    loading={isLoading}
                                >
                                    Tải lại
                                </Button>
                                <Button
                                    size="small"
                                    icon={<CheckOutlined />}
                                    onClick={handleMarkAll}
                                    disabled={!unreadCount}
                                >
                                    Đã đọc hết
                                </Button>
                            </Space>
                        </Space>
                    </div>

                    {/* Danh sách */}
                    <div
                        id="scrollableDivNotif"
                        style={{
                            maxHeight: 'calc(80vh - 50px)',
                            overflowY: 'auto',
                            padding: '10px 0',
                        }}
                    >
                        <InfiniteScroll
                            dataLength={notifications.length}
                            next={handleScrollLoad}
                            hasMore={notifications.length < pagination.total}
                            scrollableTarget="scrollableDivNotif"
                            loader={
                                <div style={{ textAlign: 'center', padding: 10 }}>
                                    <Skeleton avatar paragraph={{ rows: 1 }} active />
                                </div>
                            }
                            endMessage={
                                <p
                                    style={{
                                        textAlign: 'center',
                                        color: '#999',
                                        margin: '10px 0',
                                        fontStyle: 'italic',
                                    }}
                                >
                                    Đã tải hết thông báo
                                </p>
                            }
                        >
                            <List
                                itemLayout="horizontal"
                                size="small"
                                dataSource={notifications}
                                renderItem={(item) => (
                                    <List.Item
                                        key={item?.id}
                                        onClick={() => handleMarkRead(item?.id)}
                                        style={{
                                            cursor: 'pointer',
                                            background: item?.read_at ? '#fff' : '#e6f7ff',
                                            borderRadius: 6,
                                            marginBottom: 6,
                                            padding: '8px',
                                        }}
                                    >
                                        <Link
                                            to={item?.url || '#'}
                                            rel="noopener noreferrer"
                                            style={{ display: 'flex', flex: 1, alignItems: 'center', textDecoration: 'none', color: 'inherit' }}
                                        >
                                            <List.Item.Meta
                                                avatar={<Avatar src={logo_avatar} />}
                                                title={
                                                    <div style={{ wordBreak: 'break-word' }}>
                                                        <span className={item?.read_at ? 'readed' : 'unread'}>
                                                            {HtmlParser(getNotificationPreviewMessage(item))}
                                                        </span>
                                                    </div>
                                                }
                                                description={
                                                    <div
                                                        style={{
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            alignItems: 'center',
                                                            gap: 8
                                                        }}
                                                    >
                                                        {/* Bên trái: Thời gian */}
                                                        <span style={{ color: '#999', fontSize: 12 }}>
                                                            {moment(item?.created_at).fromNow()}
                                                        </span>

                                                        {/* Bên phải: Button */}
                                                        {item?.read_at && (
                                                            <Button
                                                                size="small"
                                                                type="text"
                                                                style={{
                                                                    fontSize: 12,
                                                                    color: '#1677ff',
                                                                    padding: 0,
                                                                    height: 'auto',
                                                                    whiteSpace: 'nowrap',
                                                                }}
                                                                onClick={(e) => {
                                                                    e.preventDefault();    // chặn Link
                                                                    e.stopPropagation();   // chặn markRead
                                                                    handleMarkUnread(item?.id);
                                                                }}
                                                            >
                                                                Đánh dấu chưa đọc
                                                            </Button>
                                                        )}
                                                    </div>
                                                }
                                            />
                                            <div className="status-notification">
                                                {item?.read_at ? (
                                                    <Badge status="default" />
                                                ) : (
                                                    <Badge color="blue" />
                                                )}
                                            </div>
                                        </Link>
                                    </List.Item>
                                )}
                            />
                        </InfiniteScroll>
                    </div>

                    {/* Footer */}
                    <div
                        style={{
                            position: 'sticky',
                            bottom: 0,
                            background: '#fff',
                            borderTop: '1px solid #f0f0f0',
                            padding: '8px 12px',
                            textAlign: 'center',
                        }}
                    >
                        <Link
                            to="/notifications?read_status=unread"
                            onClick={() => toggleVisible(false)}
                            style={{
                                display: 'inline-block',
                                color: '#1890ff',
                                fontWeight: 500,
                            }}
                        >
                            Xem tất cả thông báo →
                        </Link>
                    </div>
                </div>
            }
        >
            <Badge offset={[4, 2]} count={unreadCount} size="small" overflowCount={10}>
                <BellOutlined style={{ fontSize: 22, color: '#fff', cursor: 'pointer' }} />
            </Badge>
        </Popover>
    );
};

export default NotificationPopover;
